import React, { useContext } from "react";
import { ICON_SIZE, NextIcon } from "./actionIcons";
import ReactDOM from "react-dom";
import { CheckCircle2, Circle, Globe, X } from "lucide-react";
import { GlobalContext } from "./GlobalContext";
import { emitter } from "./UtilityFunctions";
import { useSetupCompletion } from "./hooks/useSetupCompletion";
import { SETUP_GROUPS, orderedItems } from "./setupItems";
import { requestSettingsSection } from "./settingsIntent";

/**
 * The setup checklist, as a modal you can work through.
 *
 * Opened from three places — the dashboard banner, the dashboard tile, and
 * once a session at login (SetupPrompt.jsx). One component for all three
 * because they are the same list; what differs is only the footer button, and
 * a second implementation would drift.
 *
 * "GO →" LEAVES. Each row navigates to the Settings section that fixes it and
 * closes the modal behind itself, rather than embedding the field inline. An
 * embedded editor would be a second place to change your opening hours, with
 * its own validation and its own bugs — and it would teach nobody where the
 * setting actually lives, which is the thing that stops this modal being
 * needed a third time.
 *
 * WHEN THERE IS NO PUBLIC SITE the server marks the eight website items
 * inapplicable and this renders a single explanatory row instead. A dealership
 * that has not bought a catalog site should be told that once, not asked
 * eleven times for content to put on it.
 */

export default function SetupWizardModal({ open, onClose, dismissLabel = "Close" }) {
    const { can } = useContext(GlobalContext);
    const allowed = can("settings.edit");
    const { data } = useSetupCompletion({ enabled: allowed && open });

    if (!open || !allowed || !data) return null;

    const items = orderedItems(data.items);
    const remaining = items.filter((i) => !i.done).length;

    const go = (meta) => {
        requestSettingsSection({ section: meta.section, tab: meta.tab, anchor: meta.anchor });
        emitter.emit("navigate", { tab: "tab-settings" });
        onClose();
    };

    //Grouped for scanning, but the groups are drawn from what is actually
    //applicable — an empty group is not rendered, so a dealership with no
    //public site never sees a "Your public website" heading over one row.
    const grouped = SETUP_GROUPS
        .map((group) => ({ ...group, rows: items.filter((i) => i.group === group.key) }))
        .filter((group) => group.rows.length);

    return ReactDOM.createPortal(
        <div
            style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(15, 23, 42, 0.55)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 50,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                id="setup-wizard"
                role="dialog"
                aria-label="Finish setting up your dealership"
                style={{
                    background: "var(--theme-surface)",
                    borderRadius: "var(--theme-radius-lg)",
                    width: "520px", maxWidth: "92vw",
                    boxShadow: "var(--theme-shadow-dialog)",
                    display: "flex", flexDirection: "column", maxHeight: "88vh",
                }}
            >
                <div style={{ padding: "18px 20px 12px", position: "relative" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}>
                        {remaining === 0 ? "Your setup is complete" : "Finish setting up your dealership"}
                    </h3>
                    <p className="hint-t" style={{ margin: 0 }}>
                        {remaining === 0
                            ? "Everything that affects your public site and your paperwork is filled in."
                            : `${data.score}% done — ${remaining} ${remaining === 1 ? "item" : "items"} left. Each one changes something a customer or a colleague sees.`}
                    </p>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        style={{
                            position: "absolute", top: 14, right: 14,
                            background: "none", border: "none", cursor: "pointer",
                            color: "var(--theme-muted)", padding: 2, lineHeight: 0,
                        }}
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="modal-body-padded is-scroll" style={{ paddingTop: 0 }}>
                    {grouped.map((group) => (
                        <div key={group.key} style={{ marginBottom: 14 }}>
                            <p
                                style={{
                                    fontSize: 11, fontWeight: 600, letterSpacing: "0.04em",
                                    textTransform: "uppercase", color: "var(--theme-muted)",
                                    margin: "0 0 6px",
                                }}
                            >
                                {group.label}
                            </p>
                            {group.rows.map((row) => (
                                <SetupRow key={row.key} row={row} onGo={() => go(row.meta)} />
                            ))}
                        </div>
                    ))}

                    {/* The one thing worth saying to a dealership with no
                        catalog site, in place of the eight items about what to
                        put on it. */}
                    {!data.hasPublicSite && (
                        <div
                            style={{
                                display: "flex", gap: 10, alignItems: "flex-start",
                                padding: "10px 12px", borderRadius: 8,
                                background: "var(--theme-surface-alt)",
                            }}
                        >
                            <Globe size={16} style={{ flexShrink: 0, marginTop: 2, color: "var(--theme-muted)" }} />
                            <p className="hint-t" style={{ margin: 0 }}>
                                Once you have a public listings site and its address is set above, this list will
                                also cover the logo, About page, locations, team and social links that appear on it.
                            </p>
                        </div>
                    )}
                </div>

                <div className="modal-shell-footer">
                    <button type="button" className="btn-t btn-t-secondary" id="setup-wizard-dismiss" onClick={onClose}>
                        {dismissLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

function SetupRow({ row, onGo }) {
    const Icon = row.meta.icon;
    return (
        <div
            data-setup-item={row.key}
            style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                padding: "8px 0", opacity: row.done ? 0.55 : 1,
            }}
        >
            {row.done
                ? <CheckCircle2 size={17} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                : <Circle size={17} color="#cbd5e1" style={{ flexShrink: 0, marginTop: 2 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 13.5, fontWeight: 500,
                        textDecoration: row.done ? "line-through" : "none",
                    }}
                >
                    <Icon size={13} aria-hidden="true" style={{ display: "inline", verticalAlign: "-2px", marginRight: 6, color: "var(--theme-muted)" }} />
                    {row.meta.label}
                </div>
                {/* The hint is the reason, and a done item no longer has one —
                    keeping it would leave a sentence describing a problem that
                    has been fixed. */}
                {!row.done && (
                    <p className="hint-t" style={{ margin: "2px 0 0" }}>{row.meta.hint}</p>
                )}
            </div>
            {!row.done && (
                <button
                    type="button"
                    onClick={onGo}
                    className="btn-t btn-t-secondary"
                    style={{ flexShrink: 0, padding: "2px 10px", fontSize: 12 }}
                ><NextIcon size={ICON_SIZE} />
                    Go →
                </button>
            )}
        </div>
    );
}
